/**
 * The widget's API client.
 *
 * Deliberately tiny and dependency-free. The admin app uses TanStack Query;
 * this does not, because the target is a mid-range Android on 3G and a query
 * library is 12kB gzipped to solve a problem the widget does not have — it
 * makes four requests over its whole life and none of them need caching,
 * background refetch or invalidation.
 */

import type { Locale } from '@yp/shared';

export class WidgetApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** i18n key, so the widget shows the guest their own language. */
  readonly messageKey: string | undefined;
  /** 4xx bodies carry details; a duplicate booking carries the existing one. */
  readonly details: Record<string, unknown> | undefined;

  constructor(
    status: number,
    body: {
      error?: {
        code?: string;
        message?: string;
        messageKey?: string;
        details?: Record<string, unknown>;
      };
    },
  ) {
    super(body.error?.message ?? `Request failed (${status})`);
    this.name = 'WidgetApiError';
    this.status = status;
    this.code = body.error?.code ?? 'unknown';
    this.messageKey = body.error?.messageKey;
    this.details = body.error?.details;
  }

  /**
   * The slot going while the guest typed their name.
   *
   * Both this and a duplicate booking are 409s, so the status alone is not
   * enough — the first version of this told a guest who already had a booking
   * that their table had just been taken, and sent them back to pick another
   * time they were never going to be allowed to book.
   */
  get isSlotGone(): boolean {
    return this.status === 409 && !this.isAlreadyBooked;
  }

  /** The same phone number already has a live booking for this service date. */
  get isAlreadyBooked(): boolean {
    return this.messageKey === 'errors.alreadyBooked';
  }

  /** The booking they already have, so the message can name it. */
  get existingBooking(): { booking_ref: string; starts_at: string } | null {
    const d = this.details;
    if (!d || typeof d.booking_ref !== 'string' || typeof d.starts_at !== 'string') {
      return null;
    }
    return { booking_ref: d.booking_ref, starts_at: d.starts_at };
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { query?: Record<string, string | number | undefined> } = {},
): Promise<T> {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(init.query ?? {})) {
    if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
  }

  const res = await fetch(`${url.pathname}${url.search}`, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (res.status === 204) return undefined as T;

  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new WidgetApiError(res.status, body as { error?: Record<string, string> });
  return body as T;
}

// --- shapes -----------------------------------------------------------------

export interface VenueDetail {
  slug: string;
  name: { en: string; ar: string };
  address: { en: string | null; ar: string | null };
  phone: string | null;
  currency: string;
  timezone: string;
  digit_style: 'western' | 'arabic';
  cover_image: string | null;
  whatsapp_number: string | null;
  deposit: {
    from_party_size: number;
    amount_per_cover: number;
    refundable_until_hours: number | null;
  } | null;
  rooms: Array<{ name: { en: string; ar: string } }>;
  notice: VenueNotice | null;
  notice_blocks: boolean;
}

export interface Slot {
  starts_at: string;
  ends_at: string;
  clock: string;
  shift_id: string;
  shift_name: string;
  bookable: boolean;
  confidence: 'open' | 'limited' | 'waitlist_only';
  blocked_by?: string;
}

export interface Availability {
  service_date: string;
  closed: boolean;
  closed_reason?: string;
  slots: Slot[];
}

export interface Booking {
  booking_ref: string;
  venue_name: string;
  service_date: string;
  starts_at: string;
  ends_at: string;
  clock: string;
  party_size: number;
  status: string;
  occasion: string | null;
  special_requests: string | null;
  manage_url: string;
}

export interface CreateBookingInput {
  venue_slug: string;
  service_date: string;
  starts_at: string;
  party_size: number;
  first_name: string;
  last_name?: string;
  phone: string;
  email?: string;
  locale: Locale;
  occasion?: string;
  special_requests?: string;
  /** A table the guest asks for. A request for staff, never an assignment. */
  preferred_table?: string;
  marketing_opt_in: boolean;
  source_detail?: Record<string, string>;
}

/** A branch-level message: closed for maintenance, private event, and so on. */
export interface VenueNotice {
  en: string;
  ar: string;
}

export interface VenueSummary {
  slug: string;
  name: Record<Locale, string>;
  address: Record<Locale, string | null>;
  cover_image: string | null;
  notice: VenueNotice | null;
  /** True when the notice also stops this branch taking bookings. */
  notice_blocks: boolean;
}

export const widgetApi = {
  /**
   * Used only when the widget is opened with no venue in the URL — someone
   * typed the bare domain. Every other entry point already knows the slug.
   */
  venues: () => request<{ venues: VenueSummary[] }>('/api/v1/public/venues'),

  venue: (slug: string) => request<VenueDetail>(`/api/v1/public/venues/${slug}`),

  availability: (slug: string, date: string, partySize: number, lang: Locale) =>
    request<Availability>(`/api/v1/public/venues/${slug}/availability`, {
      query: { date, party_size: partySize, lang },
    }),

  /**
   * Create the booking.
   *
   * The idempotency key is built from the guest's own inputs rather than a
   * random value, so a guest who taps Confirm twice — or whose phone retries a
   * timed-out request — gets ONE booking and the SAME reference back, instead
   * of two tables held for one party.
   */
  createBooking: (input: CreateBookingInput, idempotencyKey: string) =>
    request<Booking>('/api/v1/public/reservations', {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(input),
    }),

  joinWaitlist: (input: {
    venue_slug: string;
    service_date: string;
    party_size: number;
    first_name: string;
    phone: string;
    locale: Locale;
  }) =>
    request<{ id: string; status: string }>('/api/v1/public/waitlist', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};

/**
 * A stable idempotency key from what the guest actually chose.
 *
 * Same guest, same slot, same party = same key, so a retry replays the original
 * response. A random key per attempt would defeat the whole mechanism.
 */
export function bookingKey(input: {
  venueSlug: string;
  startsAt: string;
  partySize: number;
  phone: string;
}): string {
  const raw = `${input.venueSlug}|${input.startsAt}|${input.partySize}|${input.phone}`;
  // A short, URL-safe digest. Not cryptographic - it only has to be stable and
  // collision-resistant enough for one venue's bookings.
  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) {
    hash = (hash << 5) - hash + raw.charCodeAt(i);
    hash |= 0;
  }
  return `widget-${Math.abs(hash).toString(36)}-${raw.length}`;
}
